import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CardBase, * as Card from '@segment/matcha/recipes/Card'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import type { Startups } from '../../data'

const copy = {
  heading: `Learn how analytics can accelerate your startup`,
  description: `Get started with analytics the right way. Learn how to quantify product-market fit, design your first funnel, tracking plan, metrics, and build a data-driven company.`,
}

interface LearnHowAnalyticsProps {
  cards: Startups[`cards`]
}

const LearnHowAnalytics: FC<PropsWithChildren<LearnHowAnalyticsProps>> = ({ cards }) => {
  return (
    <Wrapper>
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Heading tag="h2">{copy.heading}</CopyText.Heading>
            <CopyText.Description>{copy.description}</CopyText.Description>
          </CopyTextBase>
        </Section.Copy>
        <Section.Contents>
          <StyledColumnTrack columns={1} overrides={{ md: 2 }}>
            {cards.map((card) => (
              <CardBase key={card.key} href={card.url} data-dynamic-link="true">
                <Card.Body>
                  <Card.Copy>
                    <Card.Headline>{card.title}</Card.Headline>
                    <Card.Description variant="pLarge">{card.description}</Card.Description>
                  </Card.Copy>
                  <Card.Action alignment="right">
                    <ChevronRightIcon />
                  </Card.Action>
                </Card.Body>
              </CardBase>
            ))}
          </StyledColumnTrack>
        </Section.Contents>
      </StyledSection>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`

const StyledColumnTrack = styled(ColumnTrack)`
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    margin-bottom: 40px;
  }
`

const StyledSection = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 40px;
  }
`

export default LearnHowAnalytics
